import * as _ from "lodash";
import * as moment from "moment";
import {BaseController} from "../../_BaseViews";
import {DashboardChannelCollection} from "../../../collections/DashboardChannels";
import {ChartConfig} from "./chart/ChartHelper";

var chartWorker = require('./chart/chartWorker');

interface ChartWorkerManagerOptions extends Backbone.ViewOptions<Backbone.Model>{
    dashboardChannelCollection?: DashboardChannelCollection
}

export interface WorkerRequestChannelInfo {
    id: number,
    sensor_type?: number,
    sensor_connector?: number,
    sensor_version?: number,
    position?: number,
    channel_secret: number,
    last_timestamp: number,
    polling_frequency: number,
    tags: any[]
}

export interface WorkerRequestParams {
    channels: {[key: number]: WorkerRequestChannelInfo},
    urlOrigin: string,
    jwt: string,
    startTimestamp: number,
    endTimestamp: number,
    keepData: boolean,
    limitDataPoints: number,
    workerId: string
}

export interface GetSeriesOptions {
    chartConfig: ChartConfig,
    startTimestamp: number,
    workerId?: string,
    endTimestamp?: number,
    keepData?: boolean,
    dashboardChannelIds?: number[],
    channels?: {[key: number]: WorkerRequestChannelInfo},
    limitDataPoints?: number,
    totalWidget?: boolean
}

class ChartWorkerPool {

    private pool: any[] = [];
    private map: { [key: string]: string[] } = {};

    constructor(private poolSize) {}

    registerWorker(workerId: string) {
        if (this.pool.length < this.poolSize) {
            this.pool.push(chartWorker());
            this.map[this.pool.length - 1] = [workerId]
        } else {
            // Find the worker with the least ids attached to it
            var minIndex;
            var minLength = Infinity;
            _.each(this.map, (ids, idx) => {
                if (ids.length < minLength) minIndex = idx;
            });

            this.map[minIndex].push(workerId);

            if (this.pool[minIndex] == null) {
                this.pool[minIndex] = chartWorker();
            }
        }

        return workerId;
    }

    deregisterWorker(workerId: string) {
        _.each(this.map, (ids, idx) => {
            if (!_.includes(ids, workerId)) return;

            this.map[idx] = _.without(this.map[idx], workerId);
            if (!this.map[idx].length) {
                this.pool[idx].terminate();
                this.pool[idx] = null;
            }
        })
    }

    getWorkerInstance(workerId: string) {
        var instance = null;
        _.each(this.map, (ids, idx) => {
            if (!_.includes(ids, workerId)) return;
            instance = this.pool[idx];
            return false;
        });
        return instance;
    }

    reset() {
        while (this.pool.length) {
            var p = this.pool.pop();
            if (p) p.terminate();
        }
        this.pool = [];
        this.map = {};
    }

}


export class ChartWorkerManager extends BaseController {

    options: ChartWorkerManagerOptions;

    private pool: ChartWorkerPool = new ChartWorkerPool(10);

    constructor(options?: ChartWorkerManagerOptions) {
        super(options);
    }

    private getUniqueId(): string {
        do {
            var uniqStr = Math.random().toString(36).substr(2, 9);
        } while(this.pool.getWorkerInstance(uniqStr));

        return uniqStr
    }

    reset() {
        this.pool.reset();
    }

    registerChannelWorker() {
        return this.pool.registerWorker(this.getUniqueId());
    }

    deRegisterWorker(workerId: string) {
        this.pool.deregisterWorker(workerId);
    }

    getSeries(options: GetSeriesOptions, callback?: (...args: any[]) => void) {
        // If no callback was passed, we're returning a deferred
        var deferred;
        if (!callback) {
            deferred = $.Deferred();
            callback= (...args) => {
                // Cut off the error argument
                if (args[0]) {
                    deferred.reject(args[0])
                } else {
                    deferred.resolve(...args.slice(1));
                }
            }
        }

        // If no workerId was passed, create a worker on the spot and tear it down afterwards
        var cb;
        if (!options.workerId) {
            options.workerId = this.registerChannelWorker();
            cb = (...args) => {
                this.deRegisterWorker(options.workerId);
                callback(...args);
            }
        } else {
            cb = callback;
        }

        if (options.dashboardChannelIds) {
            if (!this.options.dashboardChannelCollection) {
                throw new Error('Dashboard channel ids provided but no collection defined');
            }

            var channels: {[key: number]: WorkerRequestChannelInfo} = {};

            _.each(options.dashboardChannelIds, (id) => {
                // Assemble the data to be passed to the worker
                var channel = this.options.dashboardChannelCollection.findWhere({dashboard_channel_id: id});

                // In some circumstances we might not have access here, e.g. if the channel was deleted and the collection
                // has updated, but the UI has not rerendered yet
                if (!channel) return;

                var channelId = channel.get('channel_id');

                if (!channels[channelId]) {
                    channels[channelId] = {
                        id: channelId,
                        sensor_type: channel.get('sensor_type'),
                        sensor_connector: channel.get('sensor_connector'),
                        sensor_version: channel.get('sensor_version'),
                        position: channel.get('channel_position'),
                        channel_secret: channel.get('channel_secret'),
                        last_timestamp: channel.get('channel_last_timestamp') ||  moment().unix(),
                        polling_frequency: channel.get('module_polling_frequency'),
                        tags: [id]
                    };
                } else {
                    channels[channelId]['tags'].push(id);
                }
            });

            options.channels = channels;
        } else if (!options.channels) {
            throw new Error('You must provide dashboard channel ids or a channels object')
        }

        this.getSeriesForChannels(options, cb);

        // Deferred may be undefined if a callback was passed
        return deferred;
    }

    private getSeriesForChannels(options: GetSeriesOptions, callback: (...args: any[]) => void) {
        var requestParams: WorkerRequestParams = {
            channels: options.channels,
            urlOrigin: window['CONFIG'].dataApi.url,
            jwt: sessionStorage.getItem('jwt'),
            startTimestamp: options.startTimestamp,
            endTimestamp: options.endTimestamp,
            keepData: options.keepData,
            limitDataPoints: options.limitDataPoints,
            workerId: options.workerId
        };

        this.pool.getWorkerInstance(options.workerId).processData(requestParams, options, callback);
    }
}


// WEBPACK FOOTER //
// ./srv/public/js/views/dashboard/widgets/ChartWorkerManager.ts